import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronsDownUp, ChevronsUpDown, X } from "lucide-react";
import { data } from "../lib/data";
import { getProjectsForExperience } from "../lib/queries";
import { BrandLogo } from "../lib/brands";
import type { Experience } from "../types";

const TL_KEY = "atlas-timeline-collapsed-v1";

const DECADE_INSIGHTS: Record<number, string[]> = {
  2020: [
    "Generative AI (2022) shifts software development from code-first to prompt-first.",
    "AWS crosses $90B revenue in 2023 — the cloud is now the default enterprise backbone.",
    "Remote work (2020) permanently decouples geography from talent in tech teams.",
    "Amazon Connect grows to serve 10,000+ customers globally by 2023.",
  ],
  2010: [
    "Docker (2013) makes 'works on my machine' a solved problem.",
    "Salesforce crosses $1B ARR — SaaS becomes the dominant enterprise delivery model.",
    "Kubernetes (2014) turns container orchestration into an open standard.",
    "Amazon Connect (2017) brings pay-per-minute cloud contact centers to any business.",
  ],
  2000: [
    "Web 2.0 (2004) flips the web from read-only to read-write — users create content.",
    "AWS launches EC2 and S3 in 2006, effectively inventing public cloud computing.",
    "Java EE (J2EE) becomes the lingua franca of enterprise back-end development.",
    "Sarbanes-Oxley (2002) creates a decade of compliance-driven IT spend.",
  ],
  1990: [
    "Mosaic (1993) and Netscape (1994) turn the web from academic to commercial.",
    "Java 1.0 (1995) delivers 'write once, run anywhere' — and largely keeps the promise.",
    "First real e-commerce transactions clear in 1994; Amazon launches in 1995.",
    "HTML 3.2 (1997) standardizes the visual web and powers the dot-com boom.",
  ],
};

/* a dash of color per decade (same bright family as the rest of the palette) */
const DECADE_HUE: Record<number, string> = {
  2020: "#2f6fe0",
  2010: "#41a85f",
  2000: "#d9a400",
  1990: "#8e4ec6",
};

function yearOf(d: string): number {
  if (!d || d === "present") return new Date().getFullYear();
  return parseInt(d.slice(0, 4), 10);
}

function yearMonth(d: string): number {
  if (d === "present") return Number.MAX_SAFE_INTEGER;
  const [y, m] = d.split("-");
  return parseInt(y, 10) * 12 + (parseInt(m ?? "01", 10) - 1);
}

function yearSpan(start: string, end: string): string {
  const fmt = (d: string) => (d === "present" ? "" : d.slice(0, 4));
  const s = fmt(start);
  const e = fmt(end);
  return s === e ? s : `${s} – ${e}`;
}

/* newest fact first within a decade (matches the Timeline's reverse-chron order) */
function factYear(s: string): number {
  const m = String(s).match(/\b(19|20)\d{2}\b/);
  return m ? parseInt(m[0], 10) : 0;
}
function sortFactsDesc(facts: string[]): string[] {
  return [...facts].sort((a, b) => factYear(b) - factYear(a));
}

interface DecadeGroup {
  dec: number;
  items: Experience[];
}

function buildGroups(): DecadeGroup[] {
  const exps = [...data.experiences].sort(
    (a, b) => yearMonth(b.start) - yearMonth(a.start),
  );
  const groups: DecadeGroup[] = [];
  let cur: DecadeGroup | null = null;
  exps.forEach((e) => {
    const dec = Math.floor(yearOf(e.start) / 10) * 10;
    if (!cur || cur.dec !== dec) {
      cur = { dec, items: [] };
      groups.push(cur);
    }
    cur.items.push(e);
  });
  return groups;
}

interface DecadeFacts {
  dec: number;
  facts: string[];
}

function TimelineInsights({ decades }: { decades: DecadeFacts[] }) {
  const [active, setActive] = useState<number | null>(
    decades[0] ? decades[0].dec : null,
  );
  // Mobile only: the card is a slide-out drawer behind a vertical edge tab (it's too wide to sit in
  // the timeline column on a phone). Desktop ignores `open` — the card is always inline there.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  useEffect(() => {
    if (!decades.length) return;
    const onScroll = () => {
      const anchor = window.innerHeight * 0.34;
      let best = decades[0].dec;
      let bestDist = Infinity;
      for (const d of decades) {
        const el = document.querySelector(`[data-dec="${d.dec}"]`);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - anchor <= 60) {
          // this decade's header has reached the read line
          const dist = Math.abs(top - anchor);
          if (dist < bestDist || top <= anchor) {
            bestDist = dist;
            best = d.dec;
          }
        }
      }
      setActive((prev) => (prev === best ? prev : best));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [decades]);

  const cur = decades.find((d) => d.dec === active) ?? decades[0];
  if (!cur) return null;
  const hue = DECADE_HUE[cur.dec] ?? "var(--accent)";
  return (
    <div className={`tl-insights${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="tl-dyk-tab"
        style={{ "--dyk-hue": hue } as CSSProperties}
        aria-expanded={open}
        aria-controls="tl-dyk-panel"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="tl-dyk-tab-label mono">Did you know?</span>
        <span className="tl-dyk-tab-dec" style={{ color: hue }}>{`${cur.dec}s`}</span>
      </button>
      <div
        id="tl-dyk-panel"
        className="tl-insight-card card"
        style={{ "--dyk-hue": hue } as CSSProperties}
      >
        <div className="tl-insight-head">
          <span className="tl-insight-dec" key={"h" + cur.dec} style={{ color: hue }}>
            {`${cur.dec}s`}
          </span>
          <span
            className="tl-insight-badge mono"
            style={{
              color: hue,
              background: `color-mix(in oklab, ${hue} 14%, var(--card))`,
              borderColor: `color-mix(in oklab, ${hue} 30%, var(--rule))`,
            }}
          >
            Did you know?
          </span>
          <button
            type="button"
            className="tl-dyk-close"
            onClick={() => setOpen(false)}
            aria-label="Close insights"
          >
            <X size={16} />
          </button>
        </div>
        <ul className="tl-insight-list" key={cur.dec}>
          {sortFactsDesc(cur.facts).map((fact, i) => (
            <li key={i} style={{ animationDelay: `${i * 80}ms` }}>
              {fact}
            </li>
          ))}
        </ul>
        <div className="tl-insight-rail" aria-hidden="true">
          {decades.map((d) => (
            <button
              type="button"
              key={d.dec}
              className={`tl-insight-tick ${d.dec === active ? "on" : ""}`}
              title={`${d.dec}s`}
              style={{ "--dyk-hue": DECADE_HUE[d.dec] ?? "var(--accent)" } as CSSProperties}
              onClick={() => {
                const el = document.querySelector(`[data-dec="${d.dec}"]`);
                if (el)
                  window.scrollTo({
                    top:
                      el.getBoundingClientRect().top +
                      window.scrollY -
                      window.innerHeight * 0.3,
                    behavior: "smooth",
                  });
              }}
            >
              <span className="tl-insight-tick-label mono">{`${d.dec}s`}</span>
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="tl-dyk-scrim"
        aria-label="Close insights"
        tabIndex={-1}
        onClick={() => setOpen(false)}
      />
    </div>
  );
}

export function TimelinePanel() {
  const groups = useMemo(() => buildGroups(), []);
  const newestDec = groups.length ? groups[0].dec : null;
  const [collapsed, setCollapsed] = useState<Set<number>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(TL_KEY) ?? "null");
      if (Array.isArray(saved)) return new Set(saved);
    } catch {
      // fall through to the authored default
    }
    const s = new Set<number>();
    for (const g of groups) if (g.dec <= 2000 && g.dec !== newestDec) s.add(g.dec);
    return s;
  });
  useEffect(() => {
    localStorage.setItem(TL_KEY, JSON.stringify([...collapsed]));
  }, [collapsed]);
  const toggle = (dec: number) =>
    setCollapsed((prev) => {
      const n = new Set(prev);
      if (n.has(dec)) n.delete(dec);
      else n.add(dec);
      return n;
    });
  const collapsibleDecs = groups.filter((g) => g.dec !== newestDec).map((g) => g.dec);
  const anyCollapsed = collapsed.size > 0;
  const toggleAll = () =>
    setCollapsed(anyCollapsed ? new Set() : new Set(collapsibleDecs));

  let leadDone = false;
  const timelineCol = (
    <ol className="arc" style={{ marginTop: 8 }}>
      {groups.map((g) => {
        const fixed = g.dec === newestDec;
        const isCollapsed = !fixed && collapsed.has(g.dec);
        return (
          <Fragment key={g.dec}>
            <li
              className={`arc-sep ${fixed ? "" : "is-toggle"} ${isCollapsed ? "is-collapsed" : ""}`}
              data-dec={g.dec}
              onClick={fixed ? undefined : () => toggle(g.dec)}
              role={fixed ? undefined : "button"}
              tabIndex={fixed ? undefined : 0}
              onKeyDown={
                fixed
                  ? undefined
                  : (ev: KeyboardEvent<HTMLLIElement>) => {
                      if (ev.key === "Enter" || ev.key === " ") {
                        ev.preventDefault();
                        toggle(g.dec);
                      }
                    }
              }
            >
              <span className="arc-sep-label">
                {`${g.dec}s`}
                {!fixed && <ChevronDown size={15} className="arc-sep-chev" />}
                {!fixed && <span className="arc-sep-count mono">{g.items.length}</span>}
              </span>
            </li>
            {!isCollapsed &&
              g.items.map((e) => {
                const present = e.end === "present";
                const projects = getProjectsForExperience(e.id);
                const lead = !leadDone;
                leadDone = true;
                return (
                  <li className={`arc-item ${lead ? "lead" : ""}`} key={e.id}>
                    <div className="arc-rail">
                      <Link to={`/experience/${e.id}`} className="arc-node">
                        <BrandLogo company={e.company} size={lead ? 50 : 42} />
                        {present && <span className="arc-pulse live-dot" />}
                      </Link>
                    </div>
                    <Link
                      to={`/experience/${e.id}`}
                      className="arc-card card card-link card-pad"
                    >
                      <div className="arc-top">
                        <div className="arc-co">
                          {e.company}
                          {present && <span className="now-tag mono">Now</span>}
                        </div>
                        <div className="arc-dates mono">
                          {present
                            ? `${yearOf(e.start)} – Present`
                            : yearSpan(e.start, e.end)}
                        </div>
                      </div>
                      <div className="arc-role">{e.role}</div>
                      <p className="arc-summary">{e.summary}</p>
                      {projects.length > 0 && (
                        <div
                          className="mono muted"
                          style={{ fontSize: 11.5, marginTop: 10 }}
                        >
                          {projects.length} engagement{projects.length === 1 ? "" : "s"} →
                        </div>
                      )}
                    </Link>
                  </li>
                );
              })}
          </Fragment>
        );
      })}
    </ol>
  );

  const insightsCol = (
    <TimelineInsights
      decades={groups.map((g) => ({ dec: g.dec, facts: DECADE_INSIGHTS[g.dec] ?? [] }))}
    />
  );

  return (
    <div>
      <p className="section-sub" style={{ margin: 0, textAlign: "center" }}>
        <i>Step through time and see the story unfold</i>
        <br />
        <i>Each chapter leads to another. Where next?</i>
      </p>
      <div className="tl-toolbar">
        <button
          type="button"
          className="tl-expand-btn"
          onClick={toggleAll}
          title={anyCollapsed ? "Expand all decades" : "Collapse all decades"}
        >
          {anyCollapsed ? <ChevronsUpDown size={15} /> : <ChevronsDownUp size={15} />}
          {anyCollapsed ? "Expand all" : "Collapse all"}
        </button>
      </div>
      <div className="timeline-layout">
        {timelineCol}
        {insightsCol}
      </div>
    </div>
  );
}
